import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  Megaphone,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { useGetList, useNotify } from "ra-core";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { LOST_REASONS, daysSince, labelOf } from "../gulaConstants";
import { fullName, type GulaClient } from "../gulaTypes";
import { estimatedCost } from "../whatsapp/broadcasts";
import {
  buildGroups,
  type GroupKind,
  type Insight,
  type ReactivationGroup,
} from "./reactivationGroups";

const WARMTH_STYLE: Record<string, string> = {
  alta: "bg-red-500 text-white",
  media: "bg-amber-300 text-neutral-900",
  baja: "bg-muted text-muted-foreground",
};

const useInsights = () =>
  useQuery({
    queryKey: ["gula-reactivation-insights"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("gula_reactivation_insights")
        .select("client_id,pains,objection,how_to,quote,warmth,analyzed_at")
        .limit(5000);
      if (error) throw error;
      return (data ?? []) as Insight[];
    },
  });

const GroupCard = ({ group }: { group: ReactivationGroup<GulaClient> }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const quotes = group.members
    .map((member) => member.insight.quote?.trim())
    .filter((quote): quote is string => !!quote)
    .slice(0, 3);
  const prepare = () =>
    navigate(
      `/whatsapp/difusiones?grupo=${group.kind}:${group.key}&etiqueta=${encodeURIComponent(group.label)}`,
    );
  return (
    <Card className="flex flex-col gap-3 rounded-3xl p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold">{group.label}</h3>
          <p className="text-sm text-muted-foreground">
            {group.members.length} lead{group.members.length === 1 ? "" : "s"} ·
            probabilidad alta {group.warmth.alta} · media {group.warmth.media} ·
            baja {group.warmth.baja}
          </p>
        </div>
        <Button className="rounded-full" onClick={prepare}>
          <Megaphone className="h-4 w-4" />
          Preparar difusión
        </Button>
      </div>
      {quotes.length ? (
        <ul className="flex flex-col gap-1 text-sm">
          {quotes.map((quote) => (
            <li
              key={quote}
              className="italic text-neutral-700 dark:text-neutral-300"
            >
              «{quote}»
            </li>
          ))}
        </ul>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Si les llega a todos, unos {estimatedCost(group.members.length)}. Los
        que ya recibieron una difusión hace menos de 3 días se los salta.
      </p>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 self-start text-sm font-medium hover:underline"
      >
        {open ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
        {open ? "Ocultar leads" : "Ver leads y cómo reactivarlos"}
      </button>
      {open ? (
        <ul className="flex flex-col divide-y">
          {group.members.map(({ lead, insight }) => (
            <li
              key={String(lead.id)}
              className="flex flex-col gap-0.5 py-2 text-sm"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/clientes?cliente=${lead.id}`}
                  className="font-medium hover:underline"
                >
                  {fullName(lead) || "Sin nombre"}
                </Link>
                <span
                  className={cn(
                    "rounded-full px-1.5 text-xs font-semibold",
                    WARMTH_STYLE[insight.warmth ?? "media"],
                  )}
                >
                  {insight.warmth ?? "media"}
                </span>
                <span className="text-xs text-muted-foreground">
                  {lead.lost_reason
                    ? labelOf(LOST_REASONS, lead.lost_reason)
                    : "Sin motivo"}
                  {lead.reactivation_at
                    ? ` · en Reactivar hace ${daysSince(lead.reactivation_at)} d`
                    : ""}
                </span>
              </div>
              {insight.how_to ? (
                <span className="text-neutral-700 dark:text-neutral-300">
                  {insight.how_to}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
};

/**
 * Marketing → Reactivación (admins): the leads that left the setters' cycle,
 * grouped by what matters to them, to send each group the right content.
 */
export const ReactivationPage = () => {
  const notify = useNotify();
  const [kind, setKind] = useState<GroupKind>("pain");
  const [running, setRunning] = useState(false);
  const { data: leads = [] } = useGetList<GulaClient>("gula_clients_summary", {
    filter: { stage: "reactivation" },
    pagination: { page: 1, perPage: 2000 },
    sort: { field: "reactivation_at", order: "DESC" },
  });
  const { data: insights = [], refetch } = useInsights();
  const tagged = new Set(insights.map((row) => String(row.client_id)));
  const pending = leads.filter((lead) => !tagged.has(String(lead.id))).length;
  const groups = buildGroups(
    leads as (GulaClient & { id: number })[],
    insights,
    kind,
  );

  const analyse = async () => {
    setRunning(true);
    let total = 0;
    try {
      // Each run does up to 60 leads; keep going until none are left
      for (let round = 0; round < 10; round += 1) {
        const { data, error } = await getSupabaseClient().functions.invoke(
          "reactivation_insights",
          { body: { action: "run" } },
        );
        if (error) throw error;
        total += data?.analysed ?? 0;
        await refetch();
        if (!data?.left || !data?.analysed) break;
      }
      notify(`Analizados ${total} leads`, { type: "success" });
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "No se ha podido analizar",
        { type: "error" },
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Reactivación
          </h1>
          <p className="text-sm text-muted-foreground">
            {leads.length} leads en Reactivar · {leads.length - pending}{" "}
            analizados
            {pending ? ` · ${pending} sin analizar` : ""}. Si alguno responde a
            un mensaje, vuelve solo a Nuevo para los setters.
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-full"
          onClick={analyse}
          disabled={running}
        >
          {running ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          {running ? "Analizando…" : "Analizar con IA"}
        </Button>
      </div>

      <div className="flex gap-1.5">
        {(
          [
            ["pain", "Por punto de dolor"],
            ["objection", "Por objeción"],
          ] as [GroupKind, string][]
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setKind(value)}
            className={cn(
              "rounded-full border px-4 py-1.5 text-sm font-medium",
              kind === value
                ? "border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900"
                : "hover:bg-muted",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {groups.length ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {groups.map((group) => (
            <GroupCard key={`${group.kind}-${group.key}`} group={group} />
          ))}
        </div>
      ) : (
        <Card className="rounded-3xl p-6 text-sm text-muted-foreground">
          Todavía no hay grupos. Pulsa «Analizar con IA»: lee las notas de cada
          lead de Reactivar y los agrupa. Cada mañana lo hace solo con los
          nuevos.
        </Card>
      )}
      <p className="text-xs text-muted-foreground">
        Un lead puede estar en varios grupos de dolor. La IA solo lee las notas
        y el formulario: no cambia nada del lead.
      </p>
    </div>
  );
};

ReactivationPage.path = "/reactivacion";
